import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import client from '../../api/client';

function AccountSecurityMigrationPanel({ onCompleted }) {
  const [users, setUsers] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await client.get('/api/admin/account-security/unclassified-users');
      setUsers(response.data.users || []);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Bestandskonten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const setDecision = (userId, action) => {
    setDecisions((current) => ({ ...current, [userId]: action }));
    setError('');
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }
  if (users.length === 0 && !error) {
    return null;
  }

  const submit = async () => {
    const unresolved = users.filter((user) => !decisions[user.id]);
    if (unresolved.length > 0) {
      setError('Für jedes Bestandskonto ist eine Entscheidung erforderlich.');
      return;
    }
    const adopted = users.filter((user) => decisions[user.id] === 'adopt').length;
    const deactivated = users.length - adopted;
    if (!window.confirm(`${adopted} Konto/Konten übernehmen und ${deactivated} deaktivieren?`)) return;

    setSubmitting(true);
    try {
      await client.post('/api/admin/account-security/classify', {
        decisions: users.map((user) => ({ userId: user.id, action: decisions[user.id] })),
      });
      await loadUsers();
      await onCompleted();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Bestandskonten konnten nicht klassifiziert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Kontosicherheit umstellen</Typography>
            <Typography variant="body2" color="text.secondary">
              Übernommene Konten bestätigen die Passwortrichtlinie innerhalb von 30 Tagen. Deaktivierte Konten benötigen einen Aktivierungslink.
            </Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {users.map((user) => (
            <Stack
              key={user.id}
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
              sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.full_name}</Typography>
                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Button
                  type="button"
                  size="small"
                  variant={decisions[user.id] === 'adopt' ? 'contained' : 'outlined'}
                  color="primary"
                  aria-pressed={decisions[user.id] === 'adopt'}
                  onClick={() => setDecision(user.id, 'adopt')}
                  disabled={submitting}
                  sx={{ minWidth: 210 }}
                >
                  Bestandskonto übernehmen
                </Button>
                <Button
                  type="button"
                  size="small"
                  variant={decisions[user.id] === 'deactivate' ? 'contained' : 'outlined'}
                  color="error"
                  aria-pressed={decisions[user.id] === 'deactivate'}
                  onClick={() => setDecision(user.id, 'deactivate')}
                  disabled={submitting}
                  sx={{ minWidth: 130 }}
                >
                  Deaktivieren
                </Button>
              </Stack>
            </Stack>
          ))}
          <Button variant="contained" onClick={submit} disabled={submitting || users.length === 0} sx={{ alignSelf: 'flex-start' }}>
            {submitting ? 'Wird angewendet...' : 'Entscheidungen anwenden'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default AccountSecurityMigrationPanel;